<script setup lang="ts">
import { RouterView } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { onMounted, ref, watch, computed } from 'vue'
import ReportBadge from '@/components/ReportBadge.vue'

const authStore = useAuthStore()
const showWelcomeModal = ref(false)

const welcomeStorageKey = computed(() => {
  const login = authStore.user?.login
  return login ? `ui.welcomeModalSeen.${login}` : null
})

function markWelcomeSeen() {
  if (welcomeStorageKey.value) {
    localStorage.setItem(welcomeStorageKey.value, 'true')
  }
}

function closeWelcomeModal() {
  showWelcomeModal.value = false
  markWelcomeSeen()
}

watch(() => authStore.user?.login, login => {
  if (!login) {
    showWelcomeModal.value = false
    return
  }
  const key = `ui.welcomeModalSeen.${login}`
  const seen = localStorage.getItem(key)
  showWelcomeModal.value = !seen
})

onMounted(async () => {
  await authStore.checkAuth()
})
</script>

<template>
  <RouterView />

  <teleport to="body">
    <div v-if="showWelcomeModal" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black/50" @click="closeWelcomeModal"></div>
      <div class="relative w-full max-w-2xl rounded-xl bg-white shadow-xl">
        <div class="flex items-center justify-between border-b border-surface-200 px-6 py-4">
          <h2 class="text-lg font-semibold text-primary-900">Witaj w wersji demo MEDPORT</h2>
          <button class="text-primary-400 hover:text-primary-600" @click="closeWelcomeModal">✕</button>
        </div>

        <div class="max-h-[70vh] overflow-y-auto px-6 py-5 space-y-4">
          <p class="text-sm text-primary-700">
            Tu dane nie toną. Wyznaczają pewny kurs – zobacz, jak może wyglądać praca Twojej rejestracji i administracji z uporządkowaną komunikacją.
          </p>

          <hr class="border-surface-200" />

          <div class="space-y-3 text-sm text-primary-800">
            <p>
              To środowisko demo, przygotowane specjalnie do testów menedżerów i pracowników rejestracji.<br />
              Dane, które widzisz:
            </p>
            <ul class="list-disc pl-5 space-y-1">
              <li>są przykładowe (fikcyjni pacjenci, lekarze, rozmowy, wizyty),</li>
              <li>mogą być swobodnie modyfikowane,</li>
              <li>nie są nigdzie wysyłane poza tę instancję demo.</li>
            </ul>
            <p>W pełnej wersji MEDPORT:</p>
            <ul class="list-disc pl-5 space-y-1">
              <li>integrujemy się z Twoim systemem Optimmed NXT lub uruchamiamy własne rozwiązania,</li>
              <li>porządkujemy przepływ informacji między rejestracją, administracją i lekarzami,</li>
              <li>pomagamy zmniejszyć chaos i przyspieszyć obsługę pacjentów.</li>
            </ul>
            <p>
              Zapraszamy do przetestowania najważniejszych funkcji MEDPORT. Jeśli chcesz zgłosić błędy lub  propozycje zmian, korzystaj z przycisku <ReportBadge area="welcome_feedback" tip="Zgłoś uwagi dotyczące wersji demo MEDPORT" />.
            </p>
          </div>

          <hr class="border-surface-200" />

          <div class="space-y-2 text-sm text-primary-800">
            <p class="font-medium">Wprowadzenie:</p>
            <ol class="list-decimal pl-5 space-y-1">
              <li>Pulpit – zobacz, jak wygląda uporządkowany dzień pracy rejestracji.</li>
              <li>Rozmowy – przeglądaj historię komunikacji i używaj narzędzi, które ułatwiają pracę.</li>
              <li>Kontakty – odczytaj dane z różnych źródeł w jednym miejscu.</li>
              <li>Zasoby – kontroluj wykorzystanie zasobów placówki.</li>
              <li>Raporty – przejrzyj podstawowe statystyki pracy placówki.</li>
              <li>Ustawienia – sprawdź, jakie opcje konfiguracji są dostępne.</li>
            </ol>
          </div>

          <hr class="border-surface-200" />

          <p class="text-xs text-primary-500">
            Ta wersja demo ma ograniczoną funkcjonalność i nie odzwierciedla wszystkich możliwości konfiguracji, integracji i uprawnień użytkowników dostępnych w pełnym MEDPORT.
          </p>

          
        </div>

        <div class="border-t border-surface-200 px-6 py-4">
          <button
            class="w-full rounded-md bg-accent-blue px-4 py-2 text-sm font-medium text-white hover:bg-accent-blue/90"
            @click="closeWelcomeModal"
          >
            Przejdź do demo
          </button>

        </div>
      </div>
    </div>
  </teleport>
</template>
